import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import withRoleAccess from '../../hoc/withRoleAccess';
import axios from 'axios';
import getUserIdFromToken from '../../Utils/authUtils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import { Layout, Table, Select, Button, Card, Tag, Space, notification, Tabs } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import MonthlyHoursSummary from './MonthlyHoursSummary'; 
import './Teacher.css';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

const { Content } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

const generateMonthYearOptions = () => {
  const options = [];
  const startYear = 2024;
  const startMonth = 7;
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  for (let year = startYear; year <= currentYear + 5; year++) {
    for (let month = 0; month < 12; month++) {
      if (year === startYear && month < startMonth) continue;
      const monthYear = new Date(year, month).toLocaleString('default', { month: 'short', year: 'numeric' }).toUpperCase();
      options.push({ value: `${year}-${month + 1}`, label: monthYear });
      if (year === currentYear && month >= currentMonth) continue;
    }
  }

  return options;
};

const getChangeReasonDisplay = (reason) => {
  switch (reason) {
    case 'admin_adjust':
      return { text: 'Admin', color: '#005181' };
    case 'cancelled_by_student':
      return { text: 'CL by S.', color: 'red' };
    case 'cancelled_by_teacher':
      return { text: 'CL by T.', color: 'orange' };
    case 'class_on_hold_by_student':
      return { text: 'On hold by S.', color: 'blue' };
    case 'class_on_hold_by_school':
      return { text: 'On hold by P.', color: 'green' };
    case 'cancel_same_day':
      return { text: 'Cancel Same Day', color: '#FF4500' }; // Added Cancel Same Day
    case 'make_up':
      return { text: 'Make Up', color: '#8A2BE2' }; // Added Make Up
    case 'others':
      return { text: 'Others', color: 'darkgrey' };
    default:
      return { text: reason || '', color: 'black' };
  }
};


const TeacherHours = () => {
  const userId = getUserIdFromToken();
  const { t } = useTranslation();
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [selectedMonthYear, setSelectedMonthYear] = useState(`${new Date().getFullYear()}-${new Date().getMonth() + 1}`);
  const [payoutFilter, setPayoutFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const API_URL = process.env.REACT_APP_API_URL;
  const [shownToasts, setShownToasts] = useState(new Set());

  const formatDate = (dateString) => moment(dateString).format('DD MMM YYYY');

  const formatScheduleHour = (scheduleHourString) => {
    const [hours, minutes] = scheduleHourString.split(' ').map((value) => parseInt(value));
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTimeRange = (scheduleTime, scheduleHour) => {
    const startTime = moment(scheduleTime, 'HH:mm');
    const [hours, minutes] = scheduleHour.split(' ').map(value => parseInt(value));
    const endTime = startTime.clone().add(hours, 'hours').add(minutes, 'minutes');
    return `${startTime.format('HH:mm')} - ${endTime.format('HH:mm')}`;
  };

  const calculateTotalHours = (classes) => {
    return classes.reduce((total, cls) => {
      const [hours, minutes] = cls.schedule_hour.split(' ').map(value => parseInt(value));
      return total + (hours * 60 + minutes);
    }, 0);
  };

  const formatTotalHours = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  
  useEffect(() => {
    const fetchClassesWithStatus = async () => {
      const token = localStorage.getItem('token');
      try {
        const [classesRes, statusRes] = await Promise.all([
          axios.get(`${API_URL}/classes/teacher/${userId}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API_URL}/classes/${userId}/classes-status`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const classesWithStatus = classesRes.data.map(cls => {
          const statusClass = statusRes.data.find(statusCls => statusCls.classid === cls.classid);
          return {
            ...cls,
            status: statusClass ? statusClass.status : 'pending',
            payout: statusClass ? statusClass.payout : 'pending',
          };
        });
        setClasses(classesWithStatus);
        setFilteredClasses(classesWithStatus);
      } catch (error) {
        console.error(t('errorFetchingClasses'), error);
      }
    };
    fetchClassesWithStatus();
  }, [userId, API_URL, t]);

  useEffect(() => {
    const fetchClassChangeLogs = async () => {
      const token = localStorage.getItem('token');
      try {
        const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setClassChangeLogs(response.data);
      } catch (error) {
        console.error('Error fetching class change logs:', error);
      }
    };
    fetchClassChangeLogs();
  }, [API_URL]);

  useEffect(() => {
    classChangeLogs.forEach(log => {
      if (log.changereason === 'cancelled_by_student' && !shownToasts.has(log.id)) {
        toast.error(`Class ${log.class_code} was cancelled by the student.`, {
          position: "top-center",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setShownToasts(prev => new Set(prev).add(log.id));
      }
    });
  }, [classChangeLogs, shownToasts]);

  useEffect(() => {
    const applyFilters = () => {
      let filtered = classes.filter(cls => {
        const classDate = new Date(cls.date);
        const classMonthYear = `${classDate.getFullYear()}-${classDate.getMonth() + 1}`;
        const isMonthYearMatch = classMonthYear === selectedMonthYear;
        return isMonthYearMatch;
      });

      if (payoutFilter) {
        filtered = filtered.filter(cls => cls.payout === payoutFilter);
      }

      setFilteredClasses(filtered);
      setCurrentPage(1);
    };
    applyFilters();
  }, [selectedMonthYear, classes, payoutFilter]);

  const handleApprove = (classId, classDate, scheduleTime, scheduleHour) => {
    const now = moment().tz('Asia/Bangkok');
    
    // Combine date and time
    const classDateTime = moment.tz(classDate, 'Asia/Bangkok')
      .hours(parseInt(scheduleTime.split(':')[0]))
      .minutes(parseInt(scheduleTime.split(':')[1]));
      
    const [hours, minutes] = scheduleHour.split(' ').map(value => parseInt(value));
    const endTime = classDateTime.clone().add(hours, 'hours').add(minutes, 'minutes');
  
    if (now.isBefore(endTime)) {
      return notification.error({
        message: t('Cannot Approve Yet'),
        description: t('Approval is only allowed after the class session has ended.'),
      });
    }
  
    confirmAlert({
      title: t('Confirm Approval'),
      message: t('Are you sure you want to approve this class?'),
      buttons: [
        { label: t('Yes'), onClick: () => handleSubmitApproval(classId) },
        { label: t('No'), onClick: () => {} },
      ],
    });
  };
  
  const handleExportExcel = () => {
    // Transform data for Excel
    const dataForExport = filteredClasses.map(record => {
      const changeLog = classChangeLogs.find(log => log.classid === record.classid);
      const changeReason = changeLog ? changeLog.changereason : '';
      const note = changeLog?.note || '';
  
      return {
        [t('classcode')]: record.class_code,
        [t('classid')]: record.classid,
        [t('subjectName')]: record.subject_name,
        [t('date')]: formatDate(record.date),
        [t('time')]: calculateTimeRange(record.schedule_time, record.schedule_hour),
        [t('hours')]: formatScheduleHour(record.schedule_hour),
        [t('approve')]: record.status === 'approved' ? t('HOURSAPPROVED') : t('Pending'),
        [t('payment')]: record.payout === 'pending' ? t('Pending') : t('Paid'),
        [t('classchange')]: getChangeReasonDisplay(changeReason).text,
        [t('note')]: note,
      };
    });
  
    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(dataForExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Schedule');
  
    // Export to Excel
    XLSX.writeFile(workbook, 'teaching_schedule.xlsx');
  };

  const handleSubmitApproval = async (classId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/classes/${classId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      notification.success({ message: t('Class approved successfully!') });
      setClasses(prevClasses => prevClasses.map(cls => cls.classid === classId ? { ...cls, status: 'approved' } : cls));
    } catch (error) {
      notification.error({ message: t('Failed to approve class. Please try again.') });
    }
  };

  const TableSummary = ({ classes }) => {
    const totalMinutes = calculateTotalHours(classes);
    const formattedTotal = formatTotalHours(totalMinutes);
    
    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={5} className="text-right font-medium">
            {t('Total Hours for')} {moment(selectedMonthYear, 'YYYY-M').format('MMMM YYYY')}:
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} colSpan={4}>
            <span className="font-bold text-blue-800">{formattedTotal}</span>
          </Table.Summary.Cell>
        </Table.Summary.Row>
      </Table.Summary>
    );
  };


  const columns = [
    {
  title: t('classcode'),
  dataIndex: 'class_code',
  key: 'class_code',
  width: 300,
  render: (text) => (
    <div style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
      {text}
    </div>
  ),
},
    { title: t('classid'), dataIndex: 'classid', key: 'classid', ellipsis: true },
    { title: t('subjectName'), dataIndex: 'subject_name', key: 'subject_name', width: 300},
    { 
  title: t('date'),
  dataIndex: 'date',
  key: 'date',
  width: 150,
  render: (date) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatDate(date)}</div>
},
    {
  title: t('time'),
  key: 'time_range',
  width: 140,
  render: (_, record) => (
    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {calculateTimeRange(record.schedule_time, record.schedule_hour)}
    </div>
  )
},
{
  title: t('hours'),
  dataIndex: 'schedule_hour',
  key: 'schedule_hour',
  width: 80,
  render: (value) => <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{formatScheduleHour(value)}</div>
},
{
  title: t('approve'),
  key: 'approve',
  width: 150,
  render: (_, record) => (
    record.status === 'approved' ? (
      <Tag icon={<CheckCircleOutlined />} color="success">{t('HOURSAPPROVED')}</Tag>
    ) : (
      <Button
        size="small"
        onClick={() => handleApprove(record.classid, record.date, record.schedule_time, record.schedule_hour)}
      >
        {t('confirmteach')}
      </Button>
    )
  )
},
{
  title: t('payment'),
  key: 'payment',
  width: 120,
  render: (_, record) => (
    <Tag color={record.payout === 'pending' ? 'orange' : 'green'}>
      {record.payout === 'pending' ? t('Pending') : t('Paid')}
    </Tag>
  )
},
{
  title: t('classchange'),
  key: 'classchange',
  width: 150,
  render: (_, record) => {
    const changeLog = classChangeLogs.find(log => log.classid === record.classid);
    const changeReason = changeLog ? changeLog.changereason : '';
    const { text, color } = getChangeReasonDisplay(changeReason);
    return <Tag color={color}>{text}</Tag>;
  }
},
{
  title: t('note'),
  dataIndex: 'note',
  key: 'note',
  width: 200,
  render: (_, record) => {
    const changeLog = classChangeLogs.find(log => log.classid === record.classid);
    return changeLog?.note ? (
      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{changeLog.note}</div>
    ) : (
      <span className="text-gray-400">-</span>
    );
  }
}
  ];

 return (
    <Layout>
      <Sidebar />
      <Layout>
        <Header />
        <Content className="teacher-hours-container">
          <Card title={t('my_hours')}>
            <Tabs defaultActiveKey="classes">
              <TabPane tab={t('Classes')} key="classes">
                {/* Original content */}
                <Space style={{ marginBottom: 16 }}>
                  <Select
                    value={selectedMonthYear}
                    onChange={value => setSelectedMonthYear(value)}
                    style={{ width: 150 }}
                  >
                    {generateMonthYearOptions().map(option => (
                      <Option key={option.value} value={option.value}>{option.label}</Option>
                    ))}
                  </Select>
                  <Select
                    value={payoutFilter}
                    onChange={value => setPayoutFilter(value)}
                    style={{ width: 150 }}
                  >
                    <Option value="">{t('All Payouts')}</Option>
                    <Option value="pending">{t('Pending')}</Option>
                    <Option value="paid">{t('Paid')}</Option>
                  </Select>
                  <Button onClick={handleExportExcel} type="default">
                    Export to Excel
                  </Button>
                </Space>
                <div className="table-responsive">
                <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <Table
                    columns={columns}
                    dataSource={filteredClasses}
                    rowKey="classid"
                    pagination={{
                      current: currentPage,
                      pageSize: itemsPerPage,
                      total: filteredClasses.length,
                      onChange: setCurrentPage,
                      showSizeChanger: false,
                    }}
                      scroll={{ x: 'max-content' }}
                      size={window.innerWidth < 768 ? "small" : "default"}
                    summary={() => <TableSummary classes={filteredClasses} />}
                  />
                </div>
                </div>
              </TabPane>
              
              <TabPane tab={t('Monthly Summary')} key="summary">
                <MonthlyHoursSummary teacherId={userId} />
              </TabPane>
            </Tabs>
          </Card>
        </Content>
      </Layout>
    </Layout>
  );
};

export default withRoleAccess(TeacherHours, ['teacher']);